import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { uploadCatalogPdf, fetchCatalogStatus, deleteCatalogPdf, fetchSettings, uploadUrl } from "@/lib/api";

export default function CatalogUploader() {
  const [status, setStatus] = useState({ prebook: null, in_stock: null });
  const [prebookLabel, setPrebookLabel] = useState("Prebook · SS26");
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try { setStatus(await fetchCatalogStatus()); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    refresh();
    fetchSettings().then((s) => { if (s?.prebook_label) setPrebookLabel(s.prebook_label); }).catch(() => {});
  }, []);

  if (loading) return <p className="text-sm text-muted-foreground">Loading catalogs…</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <CatalogSlot type="prebook" label={prebookLabel} status={status.prebook} onChanged={refresh} />
      <CatalogSlot type="in_stock" label="In Stock Now" status={status.in_stock} onChanged={refresh} />
    </div>
  );
}

function CatalogSlot({ type, label, status, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      await uploadCatalogPdf(f, type);
      toast.success(`${label} lookbook updated`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${label} PDF?`)) return;
    try {
      await deleteCatalogPdf(type);
      toast.success(`${label} removed`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const sizeKb = status?.size ? `${Math.round(status.size / 1024)} KB` : null;
  const uploadedAt = status?.uploaded_at ? new Date(status.uploaded_at).toLocaleString() : null;

  return (
    <div className="border border-border rounded-xl p-4" data-testid={`catalog-slot-${type}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium">{label}</p>
        {status && (
          <a href={uploadUrl(status.url)} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary underline">
            View current
          </a>
        )}
      </div>

      {status ? (
        <div className="flex items-center gap-3 mb-3">
          <FileText size={28} strokeWidth={1.2} className="text-primary shrink-0" />
          <div className="min-w-0 text-xs text-muted-foreground">
            <p className="truncate text-foreground">{status.filename}</p>
            <p>{sizeKb} · uploaded {uploadedAt}</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 mb-3 text-muted-foreground">
          <FileText size={28} strokeWidth={1.2} className="shrink-0" />
          <p className="text-xs">No PDF uploaded yet</p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`catalog-upload-${type}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-foreground/20 hover:bg-foreground hover:text-background transition-colors disabled:opacity-50"
        >
          <Upload size={12} strokeWidth={1.5} />
          {busy ? "Uploading…" : status ? "Replace PDF" : "Upload PDF"}
        </button>
        {status && (
          <button
            type="button"
            onClick={handleRemove}
            data-testid={`catalog-remove-${type}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
          >
            <Trash2 size={12} strokeWidth={1.5} />
            Remove
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={handleUpload}
          data-testid={`catalog-input-${type}`}
        />
      </div>
    </div>
  );
}
